import { createScopedLogger, useApiClient, useErrorHandler } from "@@common";
import { Alert, Box, Button } from "@mui/material";
import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import { BundleClient, UserClient } from "../../api/api";
import Spinner from "../../common/components/Spinner";
import { usePersistedSetting } from "../../common/hooks/usePersistedSetting";
import AuthContext from "../../components/AuthContext";
import ConfigContext from "../../components/ConfigContext";
import { EMPTY_GUID } from "../../ExpressionsModule/Runtime/Evaluators/DomainServices/EMPTY_GUID";
import { BundleModel } from "../Models/BundleModel";
import { BundleThemeModel } from "../Models/BundleThemeModel";
import { EmbeddedResourceModel } from "../Models/EmbeddedResourceModel";
import { EnumModel } from "../Models/EnumModel";
import { SharedComponentModel } from "../Models/SharedComponentModel";
import { ViewModel } from "../Models/ViewModel";
import { DESIGNER_PROTOCOL_VERSION, MESSAGE_TYPES, type DesignerMessage } from "../Types/DesignerMessages";
import { isTrustedDesignerMessage, resolveDesignerOrigin } from "../Utils/designerAuthentication";
import { applyBundleComponentUpdateToBundleContext, applyBundleMetadataUpdateToBundleContext } from "../Utils/DesignerBundleUpdateUtils";
import { InteractivePreview, IsDesignMode } from "../Utils/runtimeDetection";
import BundleContext, { IBundleContext } from "./BundleContext";
import ReactBundleProvider from "./ReactBundleContext";

const WINDOW_MESSAGE_EVENT = "message";
const CURRENT_APP_NAME_STORAGE_KEY = "currentAppName";
const logger = createScopedLogger("BundleProvider");
type BundleLoadIssue = "signIn" | "context" | "missing" | "network";

function responseStatus(error: unknown): number | undefined {
  const response = error as { status?: number; response?: { status?: number } };
  return response?.status ?? response?.response?.status;
}

function resolveBundleThemes(bundle: BundleModel): BundleThemeModel[] {
  if (bundle.Themes && bundle.Themes.length > 0) {
    const themes = bundle.Themes.map((theme, index) => ({
      ...theme,
      Id: theme.Id || `theme-${index + 1}`,
      Name: theme.Name || (index === 0 ? "Default" : `Theme ${index + 1}`),
    }));
    const defaultTheme = themes.find((theme) => theme.IsDefault) ?? themes[0];
    return themes.map((theme) => ({ ...theme, IsDefault: theme.Id === defaultTheme.Id }));
  }

  return [{ Id: "legacy-default", Name: "Default", IsDefault: true, Theme: bundle.Theme }];
}

interface BundleProviderProps extends PropsWithChildren {}

export const BundleProvider: React.FC<BundleProviderProps> = ({ children }) => {
  const { DESIGNER_ORIGIN } = useContext(ConfigContext);
  const auth = useContext(AuthContext);
  const [value, setValue] = useState<IBundleContext | null>(null);
  const [isPending, setIsPending] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadIssue, setLoadIssue] = useState<BundleLoadIssue | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const setSelectedThemeId = useCallback((themeId: string) => {
    setValue((current) => {
      const selectedTheme = current?.Themes.find((theme) => theme.Id === themeId);
      if (!current || !selectedTheme) {
        return current;
      }

      return { ...current, SelectedThemeId: selectedTheme.Id, Theme: selectedTheme.Theme };
    });
  }, []);

  // Persist the current app name to localStorage (only used in standalone mode)
  // In design mode, the bundle comes from the designer and this is just for display
  // Enable cross-tab sync only in standalone mode to keep bundle selection consistent
  const [currentAppName, setCurrentAppName] = usePersistedSetting<string | null>(
    CURRENT_APP_NAME_STORAGE_KEY,
    null,
    !IsDesignMode // syncAcrossTabs
  );

  // IsDesignMode is a global constant (calculated once at module load time)
  // It detects whether we're running inside an iframe (designer preview) or standalone

  const bundleClient = useApiClient(BundleClient, { redirectOnUnauthorized: false });
  const userClient = useApiClient(UserClient, { redirectOnUnauthorized: false });
  const errorHandler = useErrorHandler();

  const handleAppChange = useCallback(
    (newAppName: string) => {
      logger.debug("handleAppChange called, changing from", currentAppName, "to", newAppName);
      setCurrentAppName(newAppName);
      // loadConfig will be called by the useEffect when currentAppName changes
    },
    [currentAppName, setCurrentAppName]
  );

  const loadConfig = useCallback(
    async (appName: string, availableApps: string[] = []) => {
      setIsPending(true);
      try {
        const bundle = (await (InteractivePreview.enabled
          ? bundleClient.getByDefinitionId(InteractivePreview.appId!)
          : bundleClient.getByName(appName))) as BundleModel;
        if (InteractivePreview.enabled && bundle.Id.toLowerCase() !== InteractivePreview.appId?.toLowerCase()) {
          throw new Error("The runtime returned a different App than the requested preview.");
        }

        logger.debug("Loaded bundle:", bundle.Name, "ID:", bundle.Id);

        //throw new Error("Test error handling in BundleProvider"); // Simulate error for testing
        //await delay(2000); // Simulate delay for testing purposes, remove later

        const viewById = bundle.Views.reduce(
          (acc, view) => {
            acc[view.Id] = view;
            return acc;
          },
          {} as Record<string, ViewModel>
        );

        const enumById = (bundle.Enums || []).reduce(
          (acc, enumModel) => {
            acc[enumModel.Id] = enumModel;
            return acc;
          },
          {} as Record<string, EnumModel>
        );

        const sharedComponentById = (bundle.SharedComponents || []).reduce(
          (acc, sharedComponent) => {
            acc[sharedComponent.Id] = sharedComponent;
            return acc;
          },
          {} as Record<string, SharedComponentModel>
        );

        const embededResourceById = (bundle.EmbeddedResources || []).reduce(
          (acc, resource) => {
            acc[resource.Id] = resource;
            return acc;
          },
          {} as Record<string, EmbeddedResourceModel>
        );
        const themes = resolveBundleThemes(bundle);
        const defaultTheme = themes.find((theme) => theme.IsDefault)!;

        setValue({
          Id: bundle.Id,
          Views: bundle.Views,
          ViewById: viewById,
          EnumById: enumById,
          SharedComponentById: sharedComponentById,
          ReactBundle: bundle.ReactBundle,
          Theme: defaultTheme.Theme,
          Themes: themes,
          SelectedThemeId: defaultTheme.Id,
          SetSelectedThemeId: setSelectedThemeId,
          Settings: bundle.Settings,
          EmbeddedResourceById: embededResourceById,
          CurrentAppName: bundle.Name || appName,
          AvailableApps: InteractivePreview.enabled ? [] : availableApps,
          Refresh: () => loadConfig(appName, availableApps), // Inline refresh to avoid circular dependency
          SetCurrentApp: InteractivePreview.enabled ? undefined : handleAppChange,
        });
        logger.debug("Bundle context updated, Id:", bundle.Id, "Name:", appName);
        setLoadIssue(null);
        setIsInitialLoad(false);
      } catch (error: any) {
        const status = responseStatus(error);
        if (!IsDesignMode && status === 401) {
          setLoadIssue("signIn");
        } else if (!IsDesignMode && status === 409) {
          setLoadIssue("context");
        } else if (status === 404) {
          logger.warn("App bundle not found:", appName);
          setLoadIssue("missing");
        } else {
          setLoadIssue("network");
          await errorHandler(error);
        }
        setIsInitialLoad(false);
      } finally {
        setIsPending(false);
      }
    },
    [bundleClient, errorHandler, handleAppChange, setSelectedThemeId]
  );

  const loadEmptyBundleContext = useCallback(
    async (availableApps: string[] = []) => {
      setValue({
        Id: EMPTY_GUID,
        Views: [],
        ViewById: {},
        EnumById: {},
        SharedComponentById: {},
        Themes: [],
        SetSelectedThemeId: setSelectedThemeId,
        EmbeddedResourceById: {},
        CurrentAppName: undefined,
        AvailableApps: availableApps,
        Refresh: async () => {},
        SetCurrentApp: InteractivePreview.enabled ? undefined : handleAppChange,
      });
      setIsInitialLoad(false);
      setIsPending(false);
      setLoadIssue(null);
    },
    [handleAppChange, setSelectedThemeId]
  );

  // Process designer bundle and set value
  const processDesignerBundle = React.useCallback(
    (bundle: BundleModel, appName: string) => {
      const viewById = bundle.Views.reduce(
        (acc, view) => {
          acc[view.Id] = view;
          return acc;
        },
        {} as Record<string, ViewModel>
      );

      const enumById = (bundle.Enums || []).reduce(
        (acc, enumModel) => {
          acc[enumModel.Id] = enumModel;
          return acc;
        },
        {} as Record<string, EnumModel>
      );

      const sharedComponentById = (bundle.SharedComponents || []).reduce(
        (acc, sharedComponent) => {
          acc[sharedComponent.Id] = sharedComponent;
          return acc;
        },
        {} as Record<string, SharedComponentModel>
      );

      const embededResourceById = (bundle.EmbeddedResources || []).reduce(
        (acc, resource) => {
          acc[resource.Id] = resource;
          return acc;
        },
        {} as Record<string, EmbeddedResourceModel>
      );
      const themes = resolveBundleThemes(bundle);
      const defaultTheme = themes.find((theme) => theme.IsDefault)!;

      setValue({
        Id: bundle.Id,
        Views: bundle.Views,
        ViewById: viewById,
        EnumById: enumById,
        SharedComponentById: sharedComponentById,
        ReactBundle: bundle.ReactBundle,
        Theme: defaultTheme.Theme,
        Themes: themes,
        SelectedThemeId: defaultTheme.Id,
        SetSelectedThemeId: setSelectedThemeId,
        Settings: bundle.Settings,
        EmbeddedResourceById: embededResourceById,
        CurrentAppName: appName,
        AvailableApps: [],
        Refresh: async () => {}, // No refresh in designer mode - bundle comes from designer
        SetCurrentApp: () => {}, // No app switching in designer mode
      });
      logger.debug("Designer bundle processed, Id:", bundle.Id, "Name:", appName);
      setIsInitialLoad(false);
    },
    [setSelectedThemeId]
  );

  // Use designer bundle if available, otherwise load from API
  useEffect(() => {
    if (IsDesignMode) {
      // Running in iframe - listen for SET_BUNDLE from parent
      logger.debug("Design mode - waiting for bundle from designer");

      const handleMessage = (event: MessageEvent) => {
        if (!isTrustedDesignerMessage(event, DESIGNER_ORIGIN)) return;
        if (event.data?.type === MESSAGE_TYPES.SET_BUNDLE) {
          const bundle = event.data.payload.bundle as BundleModel;
          logger.debug("Design mode - received bundle from designer:", bundle.Name);

          processDesignerBundle(bundle, bundle.Name);
          setIsPending(false);
          setIsInitialLoad(false);
        } else if (event.data?.type === MESSAGE_TYPES.BUNDLE_COMPONENT_UPDATED) {
          const { scope, ownerId, componentPath, component } = event.data.payload;
          setValue((current) => applyBundleComponentUpdateToBundleContext(current, { scope, ownerId, componentPath, component }));
        } else if (event.data?.type === MESSAGE_TYPES.BUNDLE_METADATA_UPDATED) {
          setValue((current) => applyBundleMetadataUpdateToBundleContext(current, event.data.payload));
        } else if (event.data?.type === MESSAGE_TYPES.REACT_BUILD_UPDATED) {
          const { buildId, reactBundle } = event.data.payload;
          if (reactBundle?.BuildId !== buildId) {
            logger.warn("Ignoring React build update with mismatched BuildId:", buildId, reactBundle?.BuildId);
            return;
          }

          setValue((current) => (current ? { ...current, ReactBundle: reactBundle } : current));
        }
      };

      window.addEventListener(WINDOW_MESSAGE_EVENT, handleMessage);

      // Signal to designer that runtime is ready to receive bundle
      // This handles the race condition where SET_BUNDLE was sent before we were listening
      window.parent.postMessage(
        { type: MESSAGE_TYPES.IFRAME_READY, payload: { protocolVersion: DESIGNER_PROTOCOL_VERSION } } satisfies DesignerMessage,
        resolveDesignerOrigin(DESIGNER_ORIGIN) ?? "*"
      );
      logger.debug("Design mode - sent IFRAME_READY to designer");

      // Don't set initial empty bundle or setIsPending(false) here
      // Keep showing spinner until real bundle arrives from designer
      logger.debug("Design mode - waiting for real bundle before rendering children");

      return () => window.removeEventListener(WINDOW_MESSAGE_EVENT, handleMessage);
    }
    // Design mode: only run once on mount, don't re-run when currentAppName changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleClient, processDesignerBundle, DESIGNER_ORIGIN]);

  // Standalone mode: load bundle when currentAppName changes
  useEffect(() => {
    if (!IsDesignMode) {
      const loadCurrentTenantBundle = async () => {
        try {
          if (InteractivePreview.enabled && !auth.isAuthenticated) {
            setValue(null);
            setLoadIssue("signIn");
            setIsInitialLoad(false);
            setIsPending(false);
            return;
          }
          if (InteractivePreview.enabled && !InteractivePreview.appId) {
            setLoadIssue("missing");
            setIsInitialLoad(false);
            setIsPending(false);
            return;
          }
          // A cached MSAL account is not proof that its access token is usable.
          // Preserve an already-loaded App while inviting the user to reauthenticate.
          if (auth.isAuthenticated && !(await auth.authService.getAccessToken())) {
            setLoadIssue("signIn");
            setIsInitialLoad(false);
            setIsPending(false);
            return;
          }

          let availableApps: string[];
          try {
            availableApps = (await bundleClient.getAll()) ?? [];
          } catch (error) {
            if (responseStatus(error) !== 409 || !auth.isAuthenticated) {
              throw error;
            }

            // User.GetUser selects a default environment and writes its cookie.
            // Retry once on the next request rather than treating missing context as no Apps.
            await userClient.getUser();
            availableApps = (await bundleClient.getAll()) ?? [];
          }
          if (InteractivePreview.enabled) {
            await loadConfig("", availableApps);
            return;
          }
          const appName = currentAppName != null && availableApps.includes(currentAppName) ? currentAppName : availableApps[0];

          if (!appName) {
            logger.debug("No apps are available for the current tenant");
            await loadEmptyBundleContext(availableApps);
            return;
          }

          if (appName !== currentAppName) {
            logger.debug("Selected app is not available for the current tenant, switching from", currentAppName, "to", appName);
            setCurrentAppName(appName);
            return;
          }

          logger.debug("Loading app from API:", appName);
          await loadConfig(appName, availableApps);
        } catch (error) {
          const status = responseStatus(error);
          logger.warn("Failed to load available Apps:", error);
          setLoadIssue(status === 401 ? "signIn" : status === 409 ? "context" : "network");
          setIsInitialLoad(false);
          setIsPending(false);
          if (status !== 401 && status !== 409) {
            await errorHandler(error);
          }
        }
      };

      loadCurrentTenantBundle();
    }
  }, [
    auth.isAuthenticated,
    auth.authService,
    bundleClient,
    currentAppName,
    errorHandler,
    loadConfig,
    loadEmptyBundleContext,
    reloadKey,
    setCurrentAppName,
    userClient,
  ]);

  const retryLoad = () => {
    setLoadIssue(null);
    setReloadKey((current) => current + 1);
  };

  const issueMessage =
    loadIssue === "signIn"
      ? InteractivePreview.enabled
        ? "Sign in to run the saved App preview in your selected runtime environment."
        : "Your session needs to be renewed before Apps can load."
      : loadIssue === "context"
        ? "The App environment is unavailable. Retry to restore it."
        : loadIssue === "missing"
          ? "This App could not be found in the current environment."
          : "Apps could not be loaded. Check your connection and retry.";

  const issueAlert =
    loadIssue && !IsDesignMode ? (
      <Alert
        severity={loadIssue === "missing" ? "warning" : "error"}
        role="alert"
        action={
          loadIssue === "signIn" ? (
            <Button color="inherit" size="small" onClick={() => void auth.authService.signIn(window.location.pathname + window.location.search)}>
              Sign in
            </Button>
          ) : (
            <Button color="inherit" size="small" onClick={retryLoad}>
              Retry
            </Button>
          )
        }
      >
        {issueMessage}
      </Alert>
    ) : null;

  // Only show spinner on initial load, not on subsequent bundle updates
  // This prevents flicker when properties are updated in the designer
  if (!value && issueAlert) {
    return <Box sx={{ p: 2 }}>{issueAlert}</Box>;
  }
  if (!value || (isPending && isInitialLoad)) {
    return <Spinner />;
  }
  // Key by bundle ID to force a full remount of all descendants when the bundle changes.
  // This is necessary because hooks in child components (e.g. useViewOnOpen) are conditionally
  // called based on component definitions that are assumed stable per-lifecycle. Switching bundles
  // can change those definitions (e.g. OnOpen presence), which would violate React's rules of hooks
  // if the same component fiber is reused across bundles.
  return (
    <BundleContext.Provider value={value}>
      {InteractivePreview.enabled && (
        <Alert severity="info">
          Interactive preview uses the saved App in your selected runtime environment. Chat and file actions create real resources. Unsaved designer changes are
          not included.
        </Alert>
      )}
      {issueAlert}
      <ReactBundleProvider definitionId={value.Id} definition={value.ReactBundle}>
        <React.Fragment key={value.Id}>{children}</React.Fragment>
      </ReactBundleProvider>
    </BundleContext.Provider>
  );
};

export default BundleProvider;
